import { Fragment, useState } from 'react'

import EmployerInvoiceOperationError from './EmployerInvoiceOperationError'

interface ErrorDetails {
  class?: string
  operation_name?: string
  backtrace?: string[]
  message?: string
  error_count?: number
  errors?: Array<{
    member_id: string
    member_name: string
    plan_year?: number | null
    operation_name: string
    error_message: string
    error_class: string
    backtrace?: string[]
  }>
}

interface EmployerInvoiceOperation {
  id: string
  operation_type: string
  created_at: string
  status: 'success' | 'failed'
  error_message?: string
  error_count?: number
  error_details?: ErrorDetails
  error_member_id?: string
  error_member_name?: string
  from_state: string
  to_state: string
  good_job_id?: string
  good_job_class?: string
  betterstack_logs_url?: string
}

interface Props {
  operations: EmployerInvoiceOperation[]
  attemptCount: number
  successCount: number
  failureCount: number
  lastAttemptTime?: string
  adminGoodJobPath: string
  latestGenerationErrorOperationId?: string // ID of the generation error operation displayed above
  latestFinalizationErrorOperationId?: string // ID of the finalization error operation displayed above
  latestPaymentErrorOperationId?: string // ID of the payment error operation displayed above
  adminMemberPathTemplate?: string
}

export default function EmployerInvoiceGenerationHistory ({
  operations,
  attemptCount,
  successCount,
  failureCount,
  lastAttemptTime,
  adminGoodJobPath,
  latestGenerationErrorOperationId,
  latestFinalizationErrorOperationId,
  latestPaymentErrorOperationId,
  adminMemberPathTemplate = '/admin/members/:id',
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedOperationId, setExpandedOperationId] = useState<string | null>(null)

  if (operations.length === 0) {
    return null
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  }

  const truncate = (text: string, length: number) => {
    if (text.length <= length) return text
    return text.substring(0, length) + '...'
  }

  return (
    <div className='mt-2'>
      <details open={isOpen} onToggle={(e) => setIsOpen((e.target as HTMLDetailsElement).open)}>
        <summary className='cursor-pointer text-sm text-gray-700 hover:text-gray-900'>
          View operation history ({attemptCount} {attemptCount === 1 ? 'operation' : 'operations'})
        </summary>
        <div className='mt-3 space-y-3'>
          {/* Statistics */}
          <div className='text-sm text-gray-700'>
            <span className='font-semibold'>Operations:</span> {attemptCount} total (
            <span className='text-green-600'>{successCount} successful</span>,{' '}
            <span className='text-red-600'>{failureCount} failed</span>)
            {lastAttemptTime && (
              <>
                <br />
                <span className='font-semibold'>Last operation:</span> {lastAttemptTime}
              </>
            )}
          </div>

          {/* History Table */}
          <div className='overflow-x-auto'>
            <table className='min-w-full text-sm border border-gray-200'>
              <thead className='bg-gray-100'>
                <tr>
                  <th className='px-2 py-1 text-left font-semibold'>Timestamp</th>
                  <th className='px-2 py-1 text-left font-semibold'>Status</th>
                  <th className='px-2 py-1 text-left font-semibold'>Operation Type</th>
                  <th className='px-2 py-1 text-left font-semibold'>Error</th>
                  <th className='px-2 py-1 text-left font-semibold'>Job Type</th>
                  <th className='px-2 py-1 text-left font-semibold'>Links</th>
                  <th className='px-2 py-1 text-left font-semibold'>From State</th>
                  <th className='px-2 py-1 text-left font-semibold'>To State</th>
                </tr>
              </thead>
              <tbody className='bg-white'>
                {operations.map((operation) => {
                  const isLatestGenerationError = operation.id === latestGenerationErrorOperationId
                  const isLatestFinalizationError = operation.id === latestFinalizationErrorOperationId
                  const isLatestPaymentError = operation.id === latestPaymentErrorOperationId
                  const isLatestError = isLatestGenerationError || isLatestFinalizationError || isLatestPaymentError
                  const errorElementId = isLatestGenerationError
                    ? 'employer-invoice-generation-error'
                    : isLatestFinalizationError
                      ? 'employer-invoice-finalization-error'
                      : isLatestPaymentError
                        ? 'employer-invoice-payment-error'
                        : null
                  const isExpanded = expandedOperationId === operation.id

                  return (
                    <Fragment key={operation.id}>
                      <tr className='border-t border-gray-200'>
                        <td className='px-2 py-1'>{formatTimestamp(operation.created_at)}</td>
                        <td className='px-2 py-1'>
                          <span
                            className={
                              operation.status === 'failed'
                                ? 'text-red-600 font-semibold'
                                : 'text-green-600'
                            }
                          >
                            {operation.status}
                          </span>
                        </td>
                        <td className='px-2 py-1'>
                          <span className='font-mono text-xs'>{operation.operation_type}</span>
                        </td>
                        <td className='px-2 py-1'>
                          {operation.status === 'failed' && operation.error_message && (
                            <div className='flex items-center gap-2'>
                              <span className='font-mono text-xs truncate' title={operation.error_message}>
                                {operation.error_count && operation.error_count > 1
                                  ? `Failed to process ${operation.error_count} members`
                                  : truncate(operation.error_message, 50)}
                              </span>
                              {isLatestError && errorElementId
                                ? (
                                  <button
                                    type='button'
                                    onClick={() => {
                                      const errorElement = document.getElementById(errorElementId)
                                      if (errorElement) {
                                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                        errorElement.classList.add('ring-2', 'ring-red-400', 'ring-offset-2')
                                        setTimeout(() => {
                                          errorElement.classList.remove('ring-2', 'ring-red-400', 'ring-offset-2')
                                        }, 2000)
                                      }
                                    }}
                                    className='text-xs text-blue-600 hover:underline whitespace-nowrap'
                                  >
                                    Details displayed above ↑
                                  </button>
                                  )
                                : (
                                  <button
                                    type='button'
                                    onClick={() => setExpandedOperationId(isExpanded ? null : operation.id)}
                                    className='text-xs text-blue-600 hover:underline whitespace-nowrap'
                                  >
                                    {isExpanded ? 'Hide details' : 'View details'}
                                  </button>
                                  )}
                            </div>
                          )}
                        </td>
                        <td className='px-2 py-1'>
                          {operation.good_job_class && (
                            <span className='font-mono text-xs'>{operation.good_job_class}</span>
                          )}
                        </td>
                        <td className='px-2 py-1'>
                          {operation.good_job_id && (
                            <>
                              <a
                                href={`${adminGoodJobPath}/jobs/${operation.good_job_id}`}
                                className='text-blue-600 hover:underline text-xs'
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                GJ
                              </a>
                              {operation.betterstack_logs_url && (
                                <>
                                  {' | '}
                                  <a
                                    href={operation.betterstack_logs_url}
                                    className='text-blue-600 hover:underline text-xs'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    Logs
                                  </a>
                                </>
                              )}
                            </>
                          )}
                        </td>
                        <td className='px-2 py-1'>{operation.from_state}</td>
                        <td className='px-2 py-1'>{operation.to_state}</td>
                      </tr>
                      {isExpanded && operation.error_details && (
                        <tr key={`${operation.id}-details`} className='border-t border-gray-200 bg-gray-50'>
                          <td colSpan={8} className='px-4 py-3'>
                            <EmployerInvoiceOperationError
                              operations={[
                                {
                                  id: operation.id,
                                  timestamp: operation.created_at,
                                  errorMessage: operation.error_message || '',
                                  errorMemberId: operation.error_member_id,
                                  errorMemberName: operation.error_member_name,
                                  goodJobId: operation.good_job_id,
                                  betterstackLogsUrl: operation.betterstack_logs_url,
                                  errorDetails: operation.error_details,
                                },
                              ]}
                              adminGoodJobPath={adminGoodJobPath}
                              adminMemberPathTemplate={adminMemberPathTemplate}
                              operationType={operation.operation_type as 'generation' | 'finalization'}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </details>
    </div>
  )
}
